import { type ApiResponse } from 'utils/types';
import type { BundlePromotionConfig } from 'utils/bundlePromotions';
import { retryWithBackoff } from 'utils/retry';
import { API_BASE_URL } from 'config';

// Images are returned as an array of URLs served from api.abbeydentalsupplies.com/OWPhotos
// Format: ['http://api.abbeydentalsupplies.com/OWPhotos/image1.png', ...]
// Multiple images per product are supported

// Type definitions aligned with backend models/product.model.ts

export interface VariantMeasurement {
  weight?: number;
  weightUom?: string;
  volume?: number;
  volumeUom?: string;
  length?: number;
  lengthUom?: string;
  width?: number;
  widthUom?: string;
  depth?: number;
  depthUom?: string;
  uom?: string;
}

export interface VariantCaseMeasurement {
  quantity?: number;
  weight?: number;
  width?: number;
  depth?: number;
  length?: number;
  stackable?: boolean;
}

export interface VariantStock {
  overall?: number;
  free?: number;
  salesAllocated?: number;
  salesUnallocated?: number;
  onOrder?: number;
  quarantined?: number;
  closing?: number;
}

export interface VariantPrice {
  rspExVat?: number;
  rspIncVat?: number;
  originalRspExVat?: number;
  originalRspIncVat?: number;
  discountPercent?: number;
  discountSource?: 'special' | 'price_list';
  quantityBreakApplied?: boolean;
  quantityBreakPercent?: number;
  unitPriceBeforeQuantityBreakExVat?: number;
  unitPriceBeforeQuantityBreakIncVat?: number;
  taxRate?: number; // Tax rate as percentage (e.g., 20 for 20%)
  taxRateId?: number; // Tax rate ID from vat_rate table (e.g., 2 for T1/20%)
  taxCode?: string;
  pricePer?: number;
  currencyId?: number;
  currencyName?: string;
}

export interface VariantQuantityBreak {
  quantityFrom: number;
  quantityTo: number;
  percentDiscount?: number;
  newRsp?: number;
  discountedPriceExVat?: number;
}

export interface VariantOption {
  index: number;
  name: string;
  value?: string;
  valueId?: number;
}

export interface VariantCategory {
  id: number;
  name: string;
  path: string;
  productCount?: number;
  filters?: {
    shades?: string[];
    moulds?: string[];
  };
}

// Legacy types for backward compatibility with list items
export interface ProductOption {
  name: string;
  value: string;
  valueId: number;
}

export interface ProductStock {
  overall: number;
  free: number;
  salesAllocated: number;
  salesUnallocated: number;
  onOrder: number;
  quarantined: number;
  closing: number;
}

export interface ProductPrice extends VariantPrice {}

export interface ProductCategory {
  id: number;
  name: string;
  path: string;
}

export interface ProductListItem {
  variantId: number;
  variantCode: string;
  variantDescription: string;
  productCode: string | null;
  productDescription: string | null;
  variantPriority: number | null;
  stock: ProductStock;
  price: ProductPrice | null;
  categories: ProductCategory[];
  options: ProductOption[];
  images: string[];
  documents: string[];
  quantityBreaks?: VariantQuantityBreak[];
}

export interface ProductOptionValue {
  value: string;
  valueId: number;
  variantId?: number;
  variantDescription?: string;
}

export interface ProductAggregatedOption {
  name: string;
  index: number;
  values: ProductOptionValue[];
}

// WebShopProductRecord - matches backend exactly
// Note: productCode and productDescription are removed in detail context (only in list context)
export interface WebShopProductRecord {
  variantId: number;
  variantCode: string;
  variantDescription: string;
  shortDescription?: string;
  productCode?: string; // Only used in list context, removed in detail context
  productDescription?: string; // Only used in list context, removed in detail context
  variantPriority?: string;
  measurements?: VariantMeasurement;
  caseMeasurements?: VariantCaseMeasurement;
  stock?: VariantStock;
  price?: VariantPrice;
  categories?: VariantCategory[];
  options?: VariantOption[];
  images: string[];
  documents?: string[];
  quantityBreaks?: VariantQuantityBreak[];
}

// Type for products that can be used with helper functions (list items or detail variants)
export type ProductLike = ProductListItem | WebShopProductRecord;

export interface ProductDetail {
  productId: number;
  productCode: string;
  productDescription: string;
  variant: WebShopProductRecord; // The specific variant requested (without productCode/productDescription in detail context)
  aggregatedOptions: ProductAggregatedOption[];
}

export interface ProductListResponse {
  items: ProductListItem[];
  pagination: {
    page: number;
    size: number;
    totalCount: number;
    totalPages: number;
  };
}

export interface CategoryFilter {
  shades?: string[];
  moulds?: string[];
}

export interface Category {
  id: number;
  name: string;
  path: string;
  productCount?: number;
  filters?: CategoryFilter;
}

export interface CategoriesResponse {
  categories: Category[];
}

export interface ProductListParams {
  search?: string;
  categoryName?: string;
  /** `category_treeview_node.ctn_id` — exact Webshop category match. Overrides `categoryName` when set (backend). */
  categoryId?: number;
  /** Exclude this variant from list results (e.g. related products on PDP). */
  excludeVariantId?: number;
  /** Restrict results to these variants — used by the saved items page. */
  variantIds?: number[];
  mould?: string;
  shade?: string;
  inStock?: boolean;
  page?: number;
  size?: number;
  sort?: 'latest' | 'price_asc' | 'price_desc' | 'name_asc' | 'name_desc';
  currencyId?: number;
}

class ProductService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Get access token from storage
   */
  private getAccessToken(): string | null {
    return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  }

  /**
   * Build query string from params
   */
  private buildQueryString(params: Record<string, any>): string {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'boolean') {
          queryParams.append(key, value.toString());
        } else {
          queryParams.append(key, String(value));
        }
      }
    });
    const queryString = queryParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  /**
   * Fetch products with filtering, sorting, and pagination
   * @param params - Filtering, pagination, and sort options
   * @param signal - Optional AbortSignal for request cancellation
   */
  async getProducts(params: ProductListParams = {}, signal?: AbortSignal): Promise<ProductListResponse> {
    return retryWithBackoff(
      async () => {
        const queryParams: Record<string, any> = {};
        
        if (params.search) queryParams.search = params.search;
        if (params.categoryName) queryParams.categoryName = params.categoryName;
        if (params.categoryId !== undefined) queryParams.categoryId = params.categoryId;
        if (params.excludeVariantId !== undefined) queryParams.excludeVariantId = params.excludeVariantId;
        if (params.variantIds?.length) queryParams.variantIds = params.variantIds.join(',');
        if (params.mould) queryParams.mould = params.mould;
        if (params.shade) queryParams.shade = params.shade;
        if (params.inStock !== undefined) queryParams.inStock = params.inStock.toString();
        if (params.page) queryParams.page = params.page;
        if (params.size) queryParams.size = params.size;
        if (params.sort) queryParams.sort = params.sort;
        if (params.currencyId) queryParams.currencyId = params.currencyId.toString();

        const queryString = this.buildQueryString(queryParams);
        const token = this.getAccessToken();

        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const url = `${this.baseUrl}/api/v1/product/list${queryString}`;
        const response = await fetch(url, {
          method: 'GET',
          headers,
          signal,
        });

        const data: ApiResponse<ProductListResponse> | { success: false; error: string } = await response.json();
        
        if (!response.ok || !data.success) {
          const errorMessage = 'error' in data && typeof data.error === 'string' ? data.error : 'Failed to fetch products';
          throw new Error(errorMessage);
        }

        return data.data;
      },
      {
        maxRetries: 3,
        initialDelay: 1000,
        shouldRetry: (error) => {
          // Don't retry if aborted (request cancellation)
          if (error instanceof Error && error.name === 'AbortError') {
            return false;
          }
          // Only retry on network errors or 5xx errors, not 4xx errors
          if (error instanceof TypeError) return true; // Network error
          if (error instanceof Error && error.message.includes('timeout')) return true;
          // Don't retry 4xx errors (client errors)
          return false;
        },
      }
    );
  }

  /**
   * Get all categories with their filter options
   * Includes a 5-second timeout - if the request takes longer, it will abort and throw an error
   */
  async getCategories(): Promise<Category[]> {
    return retryWithBackoff(
      async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

        try {
          const response = await fetch(`${this.baseUrl}/api/v1/product/categories`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          const data: ApiResponse<CategoriesResponse> | { success: false; error: string } = await response.json();

          if (!response.ok || !data.success) {
            const errorMessage = 'error' in data && typeof data.error === 'string' ? data.error : 'Failed to fetch categories';
            throw new Error(errorMessage);
          }

          return data.data.categories;
        } catch (error) {
          clearTimeout(timeoutId);
          
          // Handle abort error (timeout)
          if (error instanceof Error && error.name === 'AbortError') {
            throw new Error('Request timeout: Categories API took longer than 5 seconds');
          }
          
          // Re-throw other errors
          throw error;
        }
      },
      {
        maxRetries: 2,
        initialDelay: 500,
        shouldRetry: (error) => {
          // Don't retry if aborted (timeout)
          if (error instanceof Error && error.name === 'AbortError') {
            return false;
          }
          // Retry network errors
          return error instanceof TypeError;
        },
      }
    );
  }

  /**
   * Get product by variant ID
   */
  async getProductByVariantId(
    variantId: number,
    currencyId?: number,
    signal?: AbortSignal,
  ): Promise<ProductDetail> {
    return retryWithBackoff(
      async () => {
        const token = this.getAccessToken();

        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Build URL with optional currencyId query parameter
        const url = new URL(`/api/v1/product/${variantId}`, this.baseUrl || window.location.origin);
        if (currencyId) {
          url.searchParams.append('currencyId', currencyId.toString());
        }

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers,
          signal,
        });

        const data: ApiResponse<ProductDetail> | { success: false; error: string } = await response.json();

        if (!response.ok || !data.success) {
          const errorMessage = 'error' in data && typeof data.error === 'string' ? data.error : 'Failed to fetch product';
          throw new Error(errorMessage);
        }

        return data.data;
      },
      {
        maxRetries: 3,
        initialDelay: 1000,
        shouldRetry: (error) => {
          if (error instanceof Error && error.name === 'AbortError') return false;
          // Only retry on network errors, not 4xx errors
          if (error instanceof TypeError) return true; // Network error
          if (error instanceof Error && error.message.includes('timeout')) return true;
          return false; // Don't retry 4xx errors
        },
      }
    );
  }

  /**
   * Get most ordered products
   * @param limit - Maximum number of products to return (default: 20, max: 100)
   * @param currencyId - Optional currency ID (defaults to 1 for GBP)
   * @returns List of most ordered products
   */
  async getMostOrdered(
    limit: number = 20,
    currencyId?: number,
    signal?: AbortSignal,
  ): Promise<ProductListResponse> {
    return retryWithBackoff(
      async () => {
        const queryParams: Record<string, any> = {};
        
        if (limit) queryParams.limit = limit.toString();
        if (currencyId) queryParams.currencyId = currencyId.toString();

        const queryString = this.buildQueryString(queryParams);
        const token = this.getAccessToken();

        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const url = `${this.baseUrl}/api/v1/product/most-ordered${queryString}`;
        const response = await fetch(url, {
          method: 'GET',
          headers,
          signal,
        });

        const data: ApiResponse<ProductListResponse> | { success: false; error: string } = await response.json();
        
        if (!response.ok || !data.success) {
          const errorMessage = 'error' in data && typeof data.error === 'string' ? data.error : 'Failed to fetch most ordered products';
          throw new Error(errorMessage);
        }

        return data.data;
      },
      {
        maxRetries: 3,
        initialDelay: 1000,
        shouldRetry: (error) => {
          if (error instanceof Error && error.name === 'AbortError') return false;
          if (error instanceof TypeError) return true;
          if (error instanceof Error && error.message.includes('timeout')) return true;
          return false;
        },
      }
    );
  }

  /**
   * Get prices for multiple variants in a single batch request
   * OPTIMIZED: Includes request cancellation, timeout, and error handling
   * @param variantIds - Array of variant IDs to get prices for (duplicates are automatically removed)
   * @param currencyId - Optional currency ID (defaults to 1 for GBP)
   * @param signal - Optional AbortSignal for request cancellation
   * @returns Map of variantId to price information
   */
  async getBatchPrices(
    variantIds: number[],
    currencyId?: number,
    signal?: AbortSignal,
    quantities?: Record<number, number>,
  ): Promise<{
    prices: Map<number, VariantPrice>;
    quantityBreaks: Map<number, VariantQuantityBreak[]>;
  }> {
    if (variantIds.length === 0) {
      return { prices: new Map(), quantityBreaks: new Map() };
    }

    return retryWithBackoff(
      async () => {
        const token = this.getAccessToken();

        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const body: {
          currencyId?: number;
          items?: Array<{ variantId: number; quantity: number }>;
          variantIds?: number[];
        } = {};

        if (quantities && Object.keys(quantities).length > 0) {
          body.items = variantIds.map((variantId) => ({
            variantId,
            quantity: quantities[variantId] ?? 1,
          }));
        } else {
          body.variantIds = Array.from(new Set(variantIds));
        }

        if (currencyId) {
          body.currencyId = currencyId;
        }

        try {
          // OPTIMIZED: Add timeout and abort signal support
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

          // Use provided signal or create new one
          const abortSignal = signal || controller.signal;

          const response = await fetch(`${this.baseUrl}/api/v1/product/batch-prices`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
            signal: abortSignal,
          });

          clearTimeout(timeoutId);

          const data: ApiResponse<{ prices: Array<{ variantId: number; price: VariantPrice; quantityBreaks?: VariantQuantityBreak[] }> }> | { success: false; error: string } = await response.json();

          if (!response.ok || !data.success) {
            const errorMessage = 'error' in data && typeof data.error === 'string' ? data.error : 'Failed to fetch batch prices';
            throw new Error(errorMessage);
          }

          const priceMap = new Map<number, VariantPrice>();
          const quantityBreaksMap = new Map<number, VariantQuantityBreak[]>();
          data.data.prices.forEach(({ variantId, price, quantityBreaks }) => {
            priceMap.set(variantId, price);
            if (quantityBreaks?.length) {
              quantityBreaksMap.set(variantId, quantityBreaks);
            }
          });

          return { prices: priceMap, quantityBreaks: quantityBreaksMap };
        } catch (error) {
          // Handle abort/timeout errors gracefully
          if (error instanceof Error && error.name === 'AbortError') {
            throw new Error('Request timeout: Price update took longer than 30 seconds');
          }
          throw error;
        }
      },
      {
        maxRetries: 2,
        initialDelay: 1000,
        shouldRetry: (error) => {
          // Don't retry if aborted
          if (error instanceof Error && error.name === 'AbortError') {
            return false;
          }
          // Retry network errors
          return error instanceof TypeError;
        },
      }
    );
  }

  async getBundlePromotion(signal?: AbortSignal): Promise<BundlePromotionConfig> {
    const response = await fetch(`${this.baseUrl}/api/v1/product/bundle-promotion`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal,
    });

    const data: ApiResponse<BundlePromotionConfig> | { success: false; error: string } = await response.json();

    if (!response.ok || !data.success) {
      const errorMessage = 'error' in data && typeof data.error === 'string' ? data.error : 'Failed to fetch bundle promotion';
      throw new Error(errorMessage);
    }

    return data.data;
  }
}

export const productService = new ProductService();

